import { useCallback } from "react";
import { Resetter, useRecoilValue, useResetRecoilState, useSetRecoilState } from "recoil";

import {
  useSetShoppingCart,
  useShoppingCart,
} from "@features/quotes-funnel/application/shopping-cart-use-cases";
import { validatePromotionCode } from "@features/quotes-funnel/data/promotion-code-repository";
import {
  PromotionCode,
  PromotionCodeAttrs,
  PromotionCodeDTO,
} from "@features/quotes-funnel/domain/promotion-code";
import { ShoppingCart } from "@features/quotes-funnel/domain/shopping-cart";
import { promotionCodeState } from "@features/quotes-funnel/state/promotion-code-state";

export const usePromotionCode = (): PromotionCode | null => {
  const promotionCode: PromotionCodeAttrs | null = useRecoilValue(promotionCodeState);

  if (promotionCode) {
    return new PromotionCode(promotionCode);
  }

  return null;
};

export const useValidatePromotionCode = (): ((
  petParentKbKey: string,
  countryIso: string,
  code: string,
  utmCampaign?: string
) => Promise<void>) => {
  const setPromotionCode = useSetRecoilState(promotionCodeState);
  const cart: ShoppingCart = useShoppingCart();
  const updateCart = useSetShoppingCart();

  return useCallback(
    async (petParentKbKey, countryIso: string, code: string, utmCampaign = ""): Promise<void> => {
      const response: PromotionCodeDTO = await validatePromotionCode(
        JSON.stringify({
          lead_kb_key: petParentKbKey,
          country_iso: countryIso,
          code,
        })
      );

      const paymentIntervalMonths = utmCampaign ? 1 : cart.paymentIntervalMonths;
      const coupon = response.coupon;

      updateCart({ paymentIntervalMonths, coupon });
      setPromotionCode({ ...response, campaignName: utmCampaign || "" });
    },
    [setPromotionCode, cart, updateCart]
  );
};

export const useResetPromotionCode = (): (() => void) => {
  const resetDiscount: Resetter = useResetRecoilState(promotionCodeState);
  const updateCart = useSetShoppingCart();

  return useCallback(() => {
    resetDiscount();
    updateCart({ coupon: undefined });
  }, [resetDiscount, updateCart]);
};
